/**
 * THE CHECKLIST, driven by the server's own rows.
 *
 * `profile_completeness_item` is a real table with nine seeded rows, and every row is a product
 * decision an operator may change. `CompletenessItem.fieldCode` exists precisely so a client reads
 * the code rather than baking the row set in. So this module NEVER decides what the checklist
 * contains and never decides whether a row is satisfied — both come from `Application.items`.
 *
 * WHAT IT DOES DECIDE is the one thing the server cannot: which part of OUR form a given
 * `fieldCode` corresponds to, so that "A profile photo" can move focus to the upload control.
 * That map is allowed to be incomplete by construction — an operator can add a row tomorrow — so
 * an unknown code still renders its server-supplied label and simply has no jump target.
 *
 * AND THE PERCENTAGE IS NEVER OURS. "Completeness is computed at read time … it is never stored on
 * the profile", and "the SPA owns no business logic, so this percentage is the server's". This
 * module reads `Application.completenessPercent` and computes nothing.
 */
import type { TFunction } from 'i18next';
import { i18n } from '../../../i18n';
import type { Application, CompletenessItem } from '../contract';
import { narrowState } from './states';

/** The anchor ids the form sections and controls register, so a checklist row can jump to one. */
export const FIELD_ANCHORS = {
  displayName: 'apply-display-name',
  headline: 'apply-headline',
  slug: 'apply-slug',
  bioLong: 'apply-bio-long',
  bioShort: 'apply-bio-short',
  about: 'apply-about',
  expertise: 'apply-expertise',
  tags: 'apply-tags',
  avatar: 'apply-avatar',
  links: 'apply-links',
  credentials: 'apply-credentials',
  privateBlock: 'apply-private',
} as const;

/**
 * `field_code` -> the control that satisfies it. The nine keys are the nine seeded rows; a code
 * outside them is expected and handled, not treated as an error.
 */
const ANCHOR_BY_FIELD_CODE: Readonly<Record<string, string>> = {
  display_name: FIELD_ANCHORS.displayName,
  avatar: FIELD_ANCHORS.avatar,
  bio_long: FIELD_ANCHORS.bioLong,
  expertise: FIELD_ANCHORS.expertise,
  headline: FIELD_ANCHORS.headline,
  bio_short: FIELD_ANCHORS.bioShort,
  about: FIELD_ANCHORS.about,
  social_link: FIELD_ANCHORS.links,
  credential: FIELD_ANCHORS.credentials,
};

/**
 * A one-line reason, in the applicant's terms, where "why does this matter" is not obvious from
 * the label. Keyed on the server's code; a row an operator adds later simply has no hint. The
 * values are `application` catalogue keys.
 */
const HINT_BY_FIELD_CODE: Readonly<Record<string, ChecklistHintKey>> = {
  display_name: 'checklist.hint.displayName',
  avatar: 'checklist.hint.avatar',
  bio_long: 'checklist.hint.bioLong',
  expertise: 'checklist.hint.expertise',
  headline: 'checklist.hint.headline',
  bio_short: 'checklist.hint.bioShort',
  about: 'checklist.hint.about',
  social_link: 'checklist.hint.socialLink',
  credential: 'checklist.hint.credential',
};

/**
 * The two hints above that are written for a reviewer, as an APPROVED author reads them. The owner's
 * live walk of real cabinets (2026-10-01) found "This is the part editors actually read" and
 * "Somewhere we can read something you have already written" beside the profiles of authors with a
 * hundred published articles; nobody reviews their profile, readers read it. Every other hint is
 * true for both, so only these two have a second version (`rowsGateSending` picks).
 */
const AUTHOR_HINT_BY_FIELD_CODE: Readonly<Record<string, ChecklistHintKey>> = {
  bio_long: 'checklist.hint.author.bioLong',
  social_link: 'checklist.hint.author.socialLink',
};

/** A hint's key in the `application` catalogue. */
export type ChecklistHintKey =
  | 'checklist.hint.displayName'
  | 'checklist.hint.avatar'
  | 'checklist.hint.bioLong'
  | 'checklist.hint.expertise'
  | 'checklist.hint.headline'
  | 'checklist.hint.bioShort'
  | 'checklist.hint.about'
  | 'checklist.hint.socialLink'
  | 'checklist.hint.credential'
  | 'checklist.hint.author.bioLong'
  | 'checklist.hint.author.socialLink';

export interface ChecklistRow {
  readonly item: CompletenessItem;
  /** Where to send focus, when we know. Undefined for a row added after this build. */
  readonly anchorId: string | undefined;
  /** The hint's catalogue key; the checklist words it. */
  readonly hint: ChecklistHintKey | undefined;
}

/**
 * The checklist in the order the server sent it. There is NO `sortOrder` on `CompletenessItem`:
 * the array's own order is the contract ("the completeness checklist, in the order the cabinet
 * renders it"), so re-sorting it here would be inventing an order the server did not ask for.
 */
export function checklistRows(application: Application): readonly ChecklistRow[] {
  const forAuthor = !rowsGateSending(application);
  return application.items.map((item) => ({
    item,
    anchorId: ANCHOR_BY_FIELD_CODE[item.fieldCode],
    hint:
      (forAuthor ? AUTHOR_HINT_BY_FIELD_CODE[item.fieldCode] : undefined) ??
      HINT_BY_FIELD_CODE[item.fieldCode],
  }));
}

/** The four rows that gate Submit — read from the data, never from a list in this file. */
export function requiredRows(application: Application): readonly CompletenessItem[] {
  return application.items.filter((item) => item.isRequiredToSubmit);
}

/** How many of them are still unsatisfied. Zero is not the same thing as `canSubmit`. */
export function requiredOutstanding(application: Application): number {
  return requiredRows(application).filter((item) => !item.isSatisfied).length;
}

/** The optional weighted rows, which raise the percentage but never block sending. */
export function optionalRows(application: Application): readonly CompletenessItem[] {
  return application.items.filter((item) => !item.isRequiredToSubmit);
}

/**
 * ★ WHETHER THE REQUIRED ROWS ARE A GATE FOR THIS READER AT ALL (design 14, finding F3).
 *
 * For an applicant they are: the four rows are what stands between them and "Submit for review". For
 * an APPROVED author nothing on this page is sent anywhere - the page is their profile - so the
 * same rows are just part of a fuller page. The cabinet walk of 2026-10-01 opened the profile of an
 * author the import brought over from the old apps (approved, with no first-person bio - the old
 * apps never had one - and no subjects) and it said "Profile 50% complete. 2 required things still
 * to do." over a checklist headed "Four of these are needed before you can send": a to-do list for
 * an application nobody was going to send. The rows and the percentage still come from the server
 * and are still shown; only the framing that they BLOCK something follows this answer.
 */
export function rowsGateSending(application: Application): boolean {
  return narrowState(application.state) !== 'approved';
}

/**
 * A short, honest summary line for the status header.
 *
 * It deliberately reports the REQUIRED count and the percentage as two separate facts, because
 * they answer two different questions and conflating them misleads: 100% is not the gate, the
 * four required rows are, and an applicant can be at 60% and perfectly able to send. For an
 * approved author there is no gate (`rowsGateSending`), so the line is the percentage alone.
 */
export function progressSummary(
  application: Application,
  // A screen passes its own `t`; anything else reads the catalogue in the language now in force.
  t: TFunction<'application'> = i18n.getFixedT(null, 'application'),
): string {
  const outstanding = requiredOutstanding(application);
  const percent = application.completenessPercent;
  if (!rowsGateSending(application)) {
    return t('status.progress.percent', { percent });
  }
  if (outstanding === 0) {
    return t('status.progress.allRequired', { percent });
  }
  return t('status.progress.outstanding', { percent, count: outstanding });
}
